<script setup lang="ts">
/**
 * ExportModal — 1:1 порт docs/mockups/modal-export.html (inbox-189).
 *
 * Поля: формат (CSV/JSON), период, fields multiselect.
 * Endpoint /api/v1/links/export pending — submit stub'нут UiDevCard в footer.
 *
 * Open: modals.open('export', { resource: 'links' }).
 */

import { computed, ref, watch } from 'vue';
import { useModalStore } from '@/stores/modal';
import UiModal from '@/components/ui/UiModal.vue';
import UiButton from '@/components/ui/UiButton.vue';
import UiField from '@/components/ui/UiField.vue';
import UiDevCard from '@/components/ui/UiDevCard.vue';

const modals = useModalStore();

type Format = 'csv' | 'json';
type Period = '7d' | '30d' | '90d' | '365d' | 'all';

const format = ref<Format>('csv');
const period = ref<Period>('30d');
const fields = ref<string[]>(['slug', 'target_url', 'title', 'click_count', 'created_at']);
const submitting = ref(false);
const result = ref<string | null>(null);

const ALL_FIELDS = [
  { id: 'slug', label: 'Slug' },
  { id: 'target_url', label: 'Target URL' },
  { id: 'short_url', label: 'Short URL' },
  { id: 'title', label: 'Название' },
  { id: 'tags', label: 'Теги' },
  { id: 'status', label: 'Статус' },
  { id: 'click_count', label: 'Кликов' },
  { id: 'created_at', label: 'Создана' },
  { id: 'updated_at', label: 'Обновлена' },
];

watch(() => modals.activeKey, (k) => {
  if (k !== 'export') return;
  format.value = 'csv';
  period.value = '30d';
  fields.value = ['slug', 'target_url', 'title', 'click_count', 'created_at'];
  submitting.value = false;
  result.value = null;
});

const fieldsLabel = computed(() => `${fields.value.length} из ${ALL_FIELDS.length}`);

function toggleField(id: string) {
  const i = fields.value.indexOf(id);
  if (i >= 0) fields.value.splice(i, 1);
  else fields.value.push(id);
}

async function submitExport() {
  if (fields.value.length === 0) {
    alert('Выберите хотя бы одно поле.');
    return;
  }
  submitting.value = true;
  await new Promise((r) => setTimeout(r, 600));
  result.value = `Готово. ${format.value.toUpperCase()} с ${fields.value.length} колонками за период ${period.value}.`;
  submitting.value = false;
  modals.emit('export', { format: format.value, period: period.value, fields: fields.value });
}

function close() {
  if (!modals.pending) modals.close();
}
</script>

<template>
  <UiModal
    title="Экспорт ссылок"
    subtitle="CSV или JSON · до 100K строк за один экспорт"
    size="md"
    :disable-close="submitting"
    @close="close"
  >
    <form id="export-form" @submit.prevent="submitExport">
      <UiField label="Формат">
        <div class="format-tabs">
          <label :class="['tab', { active: format === 'csv' }]"><input type="radio" v-model="format" value="csv" />CSV</label>
          <label :class="['tab', { active: format === 'json' }]"><input type="radio" v-model="format" value="json" />JSON</label>
        </div>
      </UiField>

      <UiField label="Период">
        <select v-model="period" class="select">
          <option value="7d">Последние 7 дней</option>
          <option value="30d">Последние 30 дней</option>
          <option value="90d">Последние 90 дней</option>
          <option value="365d">Последний год</option>
          <option value="all">За всё время</option>
        </select>
      </UiField>

      <UiField :label="`Поля (${fieldsLabel})`">
        <div class="field-grid">
          <label v-for="f in ALL_FIELDS" :key="f.id" class="field-pill">
            <input type="checkbox" :checked="fields.includes(f.id)" @change="toggleField(f.id)" />
            <span>{{ f.label }}</span>
          </label>
        </div>
      </UiField>

      <UiDevCard
        feature="POST /api/v1/links/export"
        message="Endpoint streaming exporta пока не реализован — submit stub'нут (имитирует success после 600ms). Async job + email-notify в backend backlog."
      />

      <p v-if="result" class="result">{{ result }}</p>
    </form>

    <template #footer>
      <UiButton variant="ghost" :disabled="submitting" @click="close">Отмена</UiButton>
      <UiButton type="submit" form="export-form" :loading="submitting">Скачать</UiButton>
    </template>
  </UiModal>
</template>

<style scoped>
.format-tabs { display: flex; gap: 0; background: var(--bg-elev); border: 1px solid var(--border); border-radius: 7px; overflow: hidden; }
.format-tabs .tab { flex: 1; padding: 8px 12px; text-align: center; font-size: 13px; color: var(--fg-muted); cursor: pointer; transition: 0.12s; }
.format-tabs .tab input { display: none; }
.format-tabs .tab.active { background: var(--accent-soft); color: var(--accent-hi); font-weight: 500; }
.select { width: 100%; padding: 8px 12px; background: var(--bg-elev); border: 1px solid var(--border); border-radius: 7px; color: var(--fg); font: inherit; font-size: 13.5px; }
.field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.field-pill { display: flex; align-items: center; gap: 8px; padding: 7px 10px; background: var(--bg-elev); border: 1px solid var(--border); border-radius: 6px; font-size: 13px; color: var(--fg-muted); cursor: pointer; }
.field-pill input { accent-color: var(--accent); }
.field-pill:has(input:checked) { color: var(--fg); border-color: var(--accent-hi); background: var(--accent-soft); }
.result { font-size: 13px; color: var(--success); margin: 10px 0 0; }
</style>
